import { HandlerEvent } from 'post-robot';
import { error } from 'common/errors';
import completeCheckoutLegacy from 'communicator/external/checkout/integrated/legacy/complete-checkout-legacy';
import {
  getAccessToken,
  getApiKey,
  getCheckoutData,
  getCheckoutRequestMetadata,
  getMeta,
  getSelectCardPutIntentPayload,
  getVIntentId
} from 'communicator/store/selectors';
import { fetchPutIntent } from 'communicator/utils/api/intent';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import {
  cvvLaunchHandler,
  dcfLaunchHandler,
  fidoLaunchHandler,
  launchIssuerOtpFlowHandler,
  srcOrchestrateBankAppHandler,
  threeDsLaunchHandler,
  vppLaunchHandler
} from 'communicator/utils/launch-handler-functions';
import { ValidationError } from 'exports';
import { CheckoutCommunicatorPayload, LaunchHandlers } from 'types/visa-sdk/checkout';

type FidoCancelReason = 'skip-auth' | 'skip-register';

type BiometricsData = {
  data: {
    type: 'visa:stepup:cancel' | 'visa:stepup:complete' | 'visa:stepup:failure';
    results: {
      status?: string;
      reason?: FidoCancelReason;
      code?: string;
    };
    error?: {
      message?: string;
      reason: string;
    };
  };
  launchHandlers: LaunchHandlers;
};

export default async function cvvComplete(
  event: HandlerEvent<BiometricsData>
): Promise<CheckoutCommunicatorPayload | ValidationError> {
  const checkoutData = getCheckoutData();
  const cardIdMapping = getMeta().cardIdMapping;
  const vIntentId = getVIntentId();

  if (!checkoutData) {
    return error('CHECKOUT_FAILURE');
  }

  let selectedCardId;

  if (checkoutData.srcDigitalCardId) {
    selectedCardId = cardIdMapping[checkoutData.srcDigitalCardId] ?? checkoutData.srcDigitalCardId;
  }

  if (!selectedCardId || !vIntentId) {
    markEnd('CHECKOUT_INTEGRATED', {
      doFlush: true,
      failure: true,
      reason: 'Unable to connect to DCF'
    });

    return error('Unable to connect to DCF');
  }

  const accessToken = getAccessToken()?.token as string;

  const selectCardPutIntentPayload = await getSelectCardPutIntentPayload(
    selectedCardId,
    getCheckoutRequestMetadata()
  );

  const fidoAppResponse = event.data.data;

  if (
    fidoAppResponse.type === 'visa:stepup:complete' &&
    fidoAppResponse.results.status === 'SUCCESS' &&
    fidoAppResponse.results.code
  ) {
    selectCardPutIntentPayload.authCode = fidoAppResponse.results.code;
  }

  /* END GENAI@GHCOPILOT */
  //Remove this data for all scenarios in Registration - "Enable Now", "Maybe Later" and Authentication - "Authorize Payment"
  //Skip for "Authenticate another way" i.e. 3DS Authentication or failures i.e. Session Timeout, Unsuccessful auth
  if (
    !(
      (fidoAppResponse.type === 'visa:stepup:cancel' &&
        fidoAppResponse.results.reason === 'skip-auth') ||
      fidoAppResponse.type === 'visa:stepup:failure'
    )
  ) {
    delete selectCardPutIntentPayload.authenticationContext;
    delete selectCardPutIntentPayload.authenticationMethod;
    delete selectCardPutIntentPayload.assuranceData;
  }

  markStart('checkout_integrated_put_intent');

  const putIntentResponse = await fetchPutIntent({
    accessToken,
    apikey: getApiKey(),
    requestPayload: selectCardPutIntentPayload,
    vIntentId,
    vsbFlow: checkoutData.vsbFlow || false
  });

  markEnd('checkout_integrated_put_intent', {
    failure: !putIntentResponse.ok,
    reason: putIntentResponse.ok ? undefined : 'CHECKOUT_FAILURE'
  });

  const checkoutResponse = await completeCheckoutLegacy(
    { checkoutData, putIntentResponse },
    srcOrchestrateBankAppHandler(event.data.launchHandlers.launchSRCBankApp),
    launchIssuerOtpFlowHandler(event.data.launchHandlers.launchSRCIssuerOtp),
    cvvLaunchHandler(event.data.launchHandlers.launchCvv),
    dcfLaunchHandler(event.data.launchHandlers.launchDcf),
    fidoLaunchHandler(event.data.launchHandlers.launchFIDO),
    threeDsLaunchHandler(event.data.launchHandlers.launchThreeDs),
    threeDsLaunchHandler(event.data.launchHandlers.launchThreeDsWithOnLoad),
    vppLaunchHandler(event.data.launchHandlers.launchVPP)
  );

  if (checkoutResponse.error) {
    markEnd('CHECKOUT_INTEGRATED', {
      doFlush: true,
      failure: true,
      reason: 'CHECKOUT_FAILURE'
    });
  } else {
    markEnd('CHECKOUT_INTEGRATED', { doFlush: true });
  }

  return checkoutResponse;
}
